import { useEffect, useState } from 'react';
import { useOnboarding } from './OnboardingContext';
import TutorialModal from './TutorialModal';
import {
  WELCOME_KEY,
  welcomeSteps,
  ANALYTICS_KEY,
  analyticsSteps,
  USER_MGMT_KEY,
  userMgmtSteps,
} from './tutorials';

/**
 * OnboardingManager - Handles displaying tutorials based on onboarding status
 *
 * This component should be rendered at the app level and will show
 * appropriate tutorial modals based on the user's onboarding status.
 */
function OnboardingManager() {
  const {
    status,
    loading,
    currentTutorial,
    isTutorialCompleted,
    startTutorial,
    completeTutorial,
    skipTutorial,
    completeOnboarding,
  } = useOnboarding();

  const [activeTutorial, setActiveTutorial] = useState(null);

  // Determine which tutorial to show based on status
  useEffect(() => {
    if (loading || !status) return;

    // If user is viewing a tutorial they manually started, show that
    if (currentTutorial) {
      setActiveTutorial(currentTutorial);
      return;
    }

    // If user doesn't need onboarding, don't show anything
    if (!status.needs_onboarding) {
      setActiveTutorial(null);
      return;
    }

    // Determine which tutorial to show based on onboarding type
    const { onboarding_type } = status;

    if (onboarding_type === 'admin_setup') {
      // Admin needs to complete all three tutorials
      if (!isTutorialCompleted(WELCOME_KEY)) {
        setActiveTutorial(WELCOME_KEY);
      } else if (!isTutorialCompleted(ANALYTICS_KEY)) {
        setActiveTutorial(ANALYTICS_KEY);
      } else if (!isTutorialCompleted(USER_MGMT_KEY)) {
        setActiveTutorial(USER_MGMT_KEY);
      } else {
        // All tutorials complete - mark onboarding as done
        completeOnboarding();
        setActiveTutorial(null);
      }
    } else if (onboarding_type === 'member_setup') {
      // Member only needs welcome tutorial
      if (!isTutorialCompleted(WELCOME_KEY)) {
        setActiveTutorial(WELCOME_KEY);
      } else {
        completeOnboarding();
        setActiveTutorial(null);
      }
    } else {
      // Other onboarding types don't use tutorials
      setActiveTutorial(null);
    }
  }, [status, loading, currentTutorial, isTutorialCompleted, completeOnboarding]);

  // Get the steps for the active tutorial
  const getSteps = (tutorialKey) => {
    switch (tutorialKey) {
      case WELCOME_KEY:
        return welcomeSteps;
      case ANALYTICS_KEY:
        return analyticsSteps;
      case USER_MGMT_KEY:
        return userMgmtSteps;
      default:
        return [];
    }
  };

  const handleComplete = async (tutorialKey) => {
    await completeTutorial(tutorialKey);

    // If this was a manually started tutorial, close it
    if (currentTutorial) {
      return;
    }

    // Otherwise, the useEffect will determine what to show next
  };

  const handleSkip = async (tutorialKey) => {
    await skipTutorial(tutorialKey);

    // If this was a manually started tutorial, close it
    if (currentTutorial) {
      return;
    }

    // Otherwise, the useEffect will determine what to show next
  };

  if (!activeTutorial) {
    return null;
  }

  return (
    <TutorialModal
      isOpen={true}
      tutorialKey={activeTutorial}
      steps={getSteps(activeTutorial)}
      onComplete={handleComplete}
      onSkip={handleSkip}
    />
  );
}

export default OnboardingManager;
